import { Card, Flex, Image, List, Space, Typography } from 'antd'
import systemApi from 'api/systemApi'
import { setAppSpinning } from 'app/appSlice'
import HSMBanner from 'assets/images/Einvoice/hsm-banner.png'
import HSM from 'assets/images/Einvoice/hsm.svg'
import USBToken from 'assets/images/Einvoice/usb.svg'
import ModalConfirmCancel from 'features/System/components/Modals/ModalConfirmCancel'
import ModalChangeHSMPasscode from 'features/System/components/Modals/ModalChangeHSMPasscode'
import ModalConnectConfig from 'features/System/components/Modals/ModalConnectConfig'
import { thunkGetCompanyProfile } from 'features/System/systemSlice'
import CustomAntButton from 'general/components/Button/CustomAntButton'
import ContentContainer from 'general/components/ContentContainer'
import ModalConfirm from 'general/components/Modal/ModalConfirm'
import { DIGITAL_SIGN_METHOD } from 'general/constants/AppConstants'
import ToastHelper from 'general/helpers/ToastHelper'
import Utils from 'general/utils/Utils'
import { useAppSelector } from 'hooks/useRedux'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
const { Body, Header } = ContentContainer
const linkDownload = `${process.env.REACT_APP_BASE_URL}/resources/einvoice_tool/${process.env.REACT_APP_SIGNTOOL_LINK}.msi`
const HSM_LOCKED_ERROR = 'AUTHORIZATION IS ALREADY BLOCKED'
const HSM_LOCKED_MESSAGE =
  'Tài khoản HSM đã bị khóa. Vui lòng liên hệ bên cung cấp để mở khóa.'

const getHSMPasscodeErrorMessage = (message) =>
  typeof message === 'string' &&
  message.toUpperCase().includes(HSM_LOCKED_ERROR)
    ? HSM_LOCKED_MESSAGE
    : message

export default function DigitalSignPreference() {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { activeSigningMethod, hsmAgreementUUID, companyId, companyEmail } =
    useAppSelector((state) => state.system.company)

  const [activeId, setActiveId] = useState(null)
  const [modalState, setModalState] = useState({
    cancelConnect: false,
    connectConfig: false,
    changePasscode: false,
    forgetPasscode: false,
    forgetPasscodeSuccess: false,
  })

  const methods = useMemo(
    () => [
      {
        id: 'USB_TOKEN',
        title: 'Kí số trực tiếp qua USB TOKEN',
        thumbnail: USBToken,
        content: (
          <ol className="pl-4">
            <li>
              Máy tính bạn đang dùng cần cài đặt công cụ{' '}
              <b>{process.env.REACT_APP_SIGNTOOL_NAME || 'VietinvoiceSign'}</b>.
            </li>
            <li>
              Bạn cần cắm USB chứa chứng thư số khi thực hiện <b>ký số</b>.
            </li>
          </ol>
        ),
        isActive: activeSigningMethod === DIGITAL_SIGN_METHOD.USB,
        actions: (
          <Space>
            <CustomAntButton
              text={`Tải về ${process.env.REACT_APP_SIGNTOOL_NAME || 'VietinvoiceSign'}`}
              antProps={{
                className: 'btn btn-active-primary',
                onClick: () => open(linkDownload, '_blank'),
              }}
              customStyle={{
                boxShadow: 'none',
              }}
            />
          </Space>
        ),
      },
      {
        id: 'HSM',
        title: 'Chữ ký số HSM',
        thumbnail: HSM,
        isActive: activeSigningMethod === DIGITAL_SIGN_METHOD.HSM,
        content: (
          <>
            <Typography.Paragraph>
              Ký số thông qua máy chủ chữ ký số HSM
            </Typography.Paragraph>
            {activeSigningMethod === DIGITAL_SIGN_METHOD.HSM ? (
              <div className="bg-secondary p-3">
                <Flex align="center" gap={'1rem'} className="w-100">
                  <Image
                    src={HSMBanner}
                    alt="cyber hsm"
                    preview={false}
                    width={150}
                    height={100}
                    className="object-fit-contain"
                  />
                  <div className="d-flex flex-column py-4">
                    <span>Thông tin kết nối</span>
                    <b>{hsmAgreementUUID}</b>
                  </div>
                </Flex>
              </div>
            ) : null}
          </>
        ),
      },
    ],
    [activeSigningMethod, hsmAgreementUUID],
  )

  const handleConfirmCancel = async () => {
    try {
      await systemApi.setHSMCredential({
        hsmAgreementUUID: '',
        hsmPasscode: '',
      })
      await systemApi.setActiveSigningMethod({
        activeSigningMethod: DIGITAL_SIGN_METHOD.USB,
      })
    } catch (e) {
      console.log(e)
    }

    dispatch(thunkGetCompanyProfile({ companyId }))
    ToastHelper.showSuccess('Hủy kết nối chữ ký số HSM thành công!')

    setModalState((prev) => ({
      ...prev,
      cancelConnect: false,
    }))
  }

  const handleConnectConfig = async (values) => {
    dispatch(setAppSpinning(true))
    try {
      if (activeId == 'HSM') {
        await systemApi.setHSMCredential({
          hsmAgreementUUID: values.agreementUUID,
          hsmPasscode: values.passcode,
        })
        await systemApi.setActiveSigningMethod({
          activeSigningMethod: 'HSM',
        })
      } else {
        if (hsmAgreementUUID) {
          await systemApi.setHSMCredential({
            hsmAgreementUUID: '',
            hsmPasscode: '',
          })
        }
        await systemApi.setActiveSigningMethod({
          activeSigningMethod: DIGITAL_SIGN_METHOD.USB,
        })
      }

      dispatch(setAppSpinning(false))
      dispatch(thunkGetCompanyProfile({ companyId }))
      ToastHelper.showSuccess(
        `Kết nối chữ ký số ${
          activeId == 'HSM' ? 'HSM' : 'USB TOKEN'
        } thành công!`,
      )

      setModalState({
        ...modalState,
        connectConfig: false,
      })
    } catch (error) {
      dispatch(setAppSpinning(false))
      console.error(error)
      throw new Error(error.message)
    }
  }

  const handleChangeHSMPasscode = async (values) => {
    dispatch(setAppSpinning(true))
    try {
      const response = await systemApi.changeHSMPasscode(values)
      if (response?.result === 'failed') {
        ToastHelper.showError(getHSMPasscodeErrorMessage(response.message))
        return false
      }

      ToastHelper.showSuccess('Đổi mật khẩu HSM thành công!')
      setModalState((prev) => ({
        ...prev,
        changePasscode: false,
      }))
    } catch (error) {
      const message = error?.response?.data?.message
      if (
        typeof message === 'string' &&
        message.toUpperCase().includes(HSM_LOCKED_ERROR)
      ) {
        toast.dismiss()
        ToastHelper.showError(HSM_LOCKED_MESSAGE)
      }
      return false
    } finally {
      dispatch(setAppSpinning(false))
    }
  }

  const handleForgetHSMPasscode = async () => {
    dispatch(setAppSpinning(true))
    try {
      const response = await systemApi.forgetHSMPasscode()
      if (response?.result === 'failed') {
        ToastHelper.showError(response.message)
        return
      }

      ToastHelper.showSuccess(
        'Đã gửi thông tin đặt lại mật khẩu đã được gửi về email của bạn. Vui lòng kiểm tra hộp thư để tiếp tục.',
      )
      await systemApi.setHSMCredential({
        hsmAgreementUUID: '',
        hsmPasscode: '',
      })
      await systemApi.setActiveSigningMethod({
        activeSigningMethod: DIGITAL_SIGN_METHOD.USB,
      })
      dispatch(thunkGetCompanyProfile({ companyId }))
      setModalState((prev) => ({
        ...prev,
        forgetPasscodeSuccess: true,
      }))
    } catch (error) {
      console.error(error)
      ToastHelper.showError(
        error?.response?.data?.message ||
          error?.message ||
          'Không thể quên mật khẩu hoặc ngắt kết nối HSM. Vui lòng thử lại.',
      )
    } finally {
      dispatch(setAppSpinning(false))
    }
  }

  return (
    <ContentContainer wrapperClassName="digital-sign-screen">
      <Header
        titleContent={t('DigitalSignPreferences')}
        description={'Vui lòng chọn hình thức ký số của đơn vị đang sử dụng'}
      ></Header>
      <Body>
        <List
          grid={{ column: 1, gutter: 16 }}
          dataSource={methods}
          renderItem={({
            title,
            thumbnail,
            content,
            actions,
            isActive,
            id,
          }) => (
            <List.Item>
              <Card
                title={
                  <div>
                    <Image
                      src={thumbnail}
                      alt={title}
                      preview={false}
                      width={30}
                      height={30}
                      className="rounded-circle"
                    />
                    {title}
                  </div>
                }
                extra={
                  isActive ? (
                    <Space align="center" className="text-primary">
                      <i
                        className="fa-solid fa-check-circle"
                        style={{ color: 'inherit' }}
                      />
                      <Typography.Text strong style={{ color: 'inherit' }}>
                        Đang sử dụng
                      </Typography.Text>
                    </Space>
                  ) : null
                }
                className={Utils.cn(isActive ? 'border-primary' : '')}
              >
                {content}
                <Flex
                  justify="flex-end"
                  align="center"
                  wrap="wrap"
                  gap={'1rem'}
                  className="pt-4"
                >
                  {actions}
                  {id === 'HSM' && isActive ? (
                    <>
                      <CustomAntButton
                        text="Đổi mật khẩu"
                        antProps={{
                          type: 'default',
                          onClick: () =>
                            setModalState((prev) => ({
                              ...prev,
                              changePasscode: true,
                            })),
                        }}
                      />
                      <CustomAntButton
                        text="Quên mật khẩu"
                        antProps={{
                          type: 'default',
                          onClick: () =>
                            setModalState((prev) => ({
                              ...prev,
                              forgetPasscode: true,
                            })),
                        }}
                      />
                    </>
                  ) : null}
                  <CustomAntButton
                    text={isActive ? 'Huỷ kết nối' : 'Sử dụng ngay'}
                    antProps={{
                      type: 'default',
                      danger: isActive,
                      disabled:
                        isActive &&
                        activeSigningMethod === DIGITAL_SIGN_METHOD.USB,
                      onClick: () => {
                        setActiveId(id)
                        id === 'HSM' &&
                        activeSigningMethod === DIGITAL_SIGN_METHOD.HSM
                          ? setModalState({
                              ...modalState,
                              cancelConnect: true,
                            })
                          : setModalState({
                              ...modalState,
                              connectConfig: true,
                            })
                      },
                    }}
                  />
                </Flex>
              </Card>
            </List.Item>
          )}
        />
      </Body>

      <ModalConfirmCancel
        title={'Huỷ kết nối HSM'}
        open={modalState.cancelConnect}
        onOpenChange={(state) =>
          setModalState({ ...modalState, cancelConnect: state })
        }
        description={
          'Sau khi hủy kết nối, hệ thống sẽ tự động chuyển hình thức ký số sang "Ký trực tiếp qua USB Token". Bạn có muốn tiếp tục không?'
        }
        onClick={handleConfirmCancel}
      />

      <ModalConnectConfig
        title={
          activeId === 'HSM'
            ? 'Thiết lập kết nối tới máy chủ ký số'
            : 'Thiết lập ký số qua USB Token'
        }
        method={activeId}
        open={modalState.connectConfig}
        onOpenChange={(state) =>
          setModalState({
            ...modalState,
            connectConfig: state,
          })
        }
        onApply={handleConnectConfig}
      />

      <ModalChangeHSMPasscode
        open={modalState.changePasscode}
        onOpenChange={(state) =>
          setModalState((prev) => ({
            ...prev,
            changePasscode: state,
          }))
        }
        onApply={handleChangeHSMPasscode}
      />

      <ModalConfirm
        show={modalState.forgetPasscode}
        setShow={(show) =>
          setModalState((prev) => ({
            ...prev,
            forgetPasscode: show,
          }))
        }
        header="Quên mật khẩu"
        content="Thông tin đặt lại mật khẩu sẽ được gửi về email đăng ký chữ ký số HSM. Bạn có muốn tiếp tục?"
        confirmText="Gửi"
        cancelText="Hủy"
        handleConfirm={handleForgetHSMPasscode}
      />

      <ModalConfirm
        show={modalState.forgetPasscodeSuccess}
        setShow={(show) =>
          setModalState((prev) => ({
            ...prev,
            forgetPasscodeSuccess: show,
          }))
        }
        header="Quên mật khẩu thành công"
        content="Quên mật khẩu thành công. Đã ngắt kết nối chữ ký số HSM. Vui lòng kết nối lại"
        confirmText="Đóng"
        cancelText="Đóng"
        handleConfirm={() => {}}
      />
    </ContentContainer>
  )
}
